import { Icon } from '@/components/ds'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export interface CampoAccesoProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'id'> {
  id: string
  label: string
  value: string
  /** Emite el valor crudo del input. El formateo lo decide quien lo usa. */
  onChange: (valor: string) => void
  /** Glifo decorativo dentro del recuadro, a la izquierda. */
  icono?: string
  /** Aclaración corta al lado del label, ej. "opcional". */
  hint?: string
  /** Dibuja el check teal a la derecha del label: el dato ya es válido. */
  listo?: boolean
  /** Slot a la derecha del label. Pisa a `listo` — ej. "¿La olvidaste?". */
  accion?: React.ReactNode
  /** Mensaje de error bajo el campo. Vacío = sin error. */
  error?: string
  /** Aclaración permanente bajo el campo. */
  nota?: string
  /** 38px en vez de 40px. Para formularios largos como el registro. */
  denso?: boolean
  /** Tipografía monoespaciada — RUT y correos, que se leen carácter a carácter. */
  mono?: boolean
  /** Contenido al final del recuadro, ej. el botón de mostrar contraseña. */
  sufijo?: React.ReactNode
}

/**
 * Campo de las pantallas de acceso: label con slots + recuadro con glifo +
 * error inline.
 *
 * Por qué no es el `<Input>` del DS a secas: acá el glifo va DENTRO del
 * recuadro y el label lleva un acompañante a la derecha (el check de validado,
 * "¿La olvidaste?", el medidor de fuerza). Eso convierte al recuadro en el
 * elemento con borde y foco, y al `<input>` en un campo desnudo adentro — de
 * ahí el `focus-within` en el contenedor en vez del `focus-visible` del input.
 *
 * El borde cuenta tres estados, y esa es la mitad del valor del componente:
 * vacío (gris claro), con contenido (gris un punto más firme, "esto ya lo
 * llenaste") y con error (rojo). El del medio es el que hace que un formulario
 * largo se lea de un vistazo.
 */
export function CampoAcceso({
  id,
  label,
  value,
  onChange,
  icono,
  hint,
  listo = false,
  accion,
  error,
  nota,
  denso = false,
  mono = false,
  sufijo,
  className,
  disabled,
  ...rest
}: CampoAccesoProps) {
  const idError = `${id}-error`
  const idNota = `${id}-nota`
  const descritoPor = [error ? idError : null, nota ? idNota : null].filter(Boolean).join(' ')

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline gap-[7px]">
        <Label htmlFor={id} className="text-strong shrink-0 whitespace-nowrap">
          {label}
        </Label>
        {hint && <span className="text-soft text-[11px]">{hint}</span>}
        <div className="flex-1" />
        {accion ?? (listo && <Icon name="check_circle" size={15} className="text-primary" />)}
      </div>

      <div
        className={cn(
          'bg-background flex items-center overflow-hidden rounded-lg border transition-colors',
          'focus-within:border-primary focus-within:ring-ring focus-within:ring-2',
          denso ? 'h-[38px]' : 'h-10',
          error ? 'border-destructive' : value ? 'border-input-filled' : 'border-input',
          disabled && 'bg-surface-muted'
        )}
      >
        {icono && (
          <Icon
            name={icono}
            size={denso ? 17 : 18}
            className={cn('text-icon-soft shrink-0', denso ? 'ml-2.5' : 'ml-[11px]')}
          />
        )}
        <input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={descritoPor || undefined}
          className={cn(
            'h-full min-w-0 flex-1 border-none bg-transparent outline-none',
            'placeholder:text-icon-soft disabled:cursor-not-allowed',
            denso ? 'px-2.5 text-[13.5px]' : 'px-[11px] text-[14px]',
            mono && 'fc-num',
            className
          )}
          {...rest}
        />
        {sufijo}
      </div>

      {error && (
        <div id={idError} className="flex items-center gap-[5px]">
          <Icon name="error" size={14} className="text-destructive" />
          <span className="text-destructive text-[11px]">{error}</span>
        </div>
      )}
      {nota && !error && (
        <span id={idNota} className="text-soft text-[11px] leading-[1.45]">
          {nota}
        </span>
      )}
    </div>
  )
}

/** Botón de mostrar/ocultar contraseña, pensado para el `sufijo` de `CampoAcceso`. */
export function BotonVerContrasena({
  visible,
  onToggle,
  denso = false,
}: {
  visible: boolean
  onToggle: () => void
  denso?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      className={cn(
        'text-soft hover:text-foreground flex h-full shrink-0 items-center justify-center transition-colors',
        denso ? 'w-8' : 'w-[34px]'
      )}
    >
      <Icon name={visible ? 'visibility_off' : 'visibility'} size={denso ? 17 : 18} />
    </button>
  )
}
